/**
 * JobNotesPanel
 *
 * Notes tab of JobDetailDialog, extracted Feb 2026. Owns the in-dialog
 * UX for: composing a new note (with reply-threading and notify-target
 * dropdown), inline edit of own notes, delete, forward-to-teammate via
 * ForwardNoteDialog, and the "mark notes as seen" side-effect that
 * dismisses the red unread pill on the Jobs list.
 *
 * Parent keeps `notes` on localJob (Activity tab merges them into its
 * combined feed). We mutate via the setNotes(updater) callback so the
 * parent stays the single source of truth.
 *
 * Backend (see /app/backend/routes/job_collab.py):
 *   POST   /api/jobs/{id}/notes
 *   PATCH  /api/jobs/{id}/notes/{noteId}
 *   DELETE /api/jobs/{id}/notes/{noteId}
 *   POST   /api/jobs/{id}/notes/{noteId}/forward
 *   POST   /api/jobs/{id}/notes/mark-seen
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ForwardNoteDialog from './ForwardNoteDialog';
import {
  Send, Trash2, CornerDownRight, BellRing, Edit2, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = window.__BACKEND_URL__;

// ── helpers (kept local so the panel is self-contained) ─────────────────
const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};
const fmtFull = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
};

export default function JobNotesPanel({
  jobId,
  notes,
  setNotes,
  currentUser,
  writable,
  isActive,
  onMutated,
}) {
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // notifyTo: 'none' | 'assignee' | 'supervisor' | 'admin' | 'all'
  const [notifyTo, setNotifyTo] = useState('none');
  const [replyToNote, setReplyToNote] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [forwardingNote, setForwardingNote] = useState(null);

  // Fire-and-forget: when the user opens the Notes tab, mark this job's
  // notes as seen so the red unread pill on the Jobs list goes away.
  // Best-effort — failure is silently swallowed; the worst case is the
  // pill stays visible.
  useEffect(() => {
    if (!isActive || !jobId) return;
    const token = localStorage.getItem('token');
    axios.post(
      `${API}/api/jobs/${jobId}/notes/mark-seen`,
      null,
      { headers: { Authorization: `Bearer ${token}` } },
    ).catch(() => { /* ignore */ });
  }, [isActive, jobId]);

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      // Translate the single-select dropdown ("none" / "assignee" / ...) into
      // the array shape the backend expects ([] = silent).
      const notify_to = notifyTo && notifyTo !== 'none' ? [notifyTo] : [];
      const { data } = await axios.post(
        `${API}/api/jobs/${jobId}/notes`,
        { text, notify_to, reply_to_note_id: replyToNote?.id || null },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      setNotes((prev) => [...(prev || []), data]);
      setNewNote('');
      setReplyToNote(null);
      onMutated?.();
      if (notify_to.length > 0) {
        toast.success('Note sent — assignee/team has been notified by bell + email.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/jobs/${jobId}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes((prev) => (prev || []).filter((n) => n.id !== noteId));
      onMutated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete note');
    }
  };

  const beginEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || '');
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const commitEditNote = async () => {
    const text = editingNoteText.trim();
    if (!editingNoteId || !text) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API}/api/jobs/${jobId}/notes/${editingNoteId}`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNotes((prev) => (prev || []).map((n) => n.id === editingNoteId ? { ...n, ...data } : n));
      cancelEditNote();
      onMutated?.();
      toast.success('Note updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save edit');
    } finally {
      setSavingNote(false);
    }
  };

  const handleForwardNote = async (recipientIds) => {
    if (!forwardingNote || !Array.isArray(recipientIds) || recipientIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/api/jobs/${jobId}/notes/${forwardingNote.id}/forward`,
        { recipient_user_ids: recipientIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Update the note's notify_recipient_ids locally so the "notified"
      // badge appears immediately.
      setNotes((prev) => (prev || []).map((n) =>
        n.id === forwardingNote.id
          ? { ...n, notify_recipient_ids: data.notify_recipient_ids || n.notify_recipient_ids }
          : n
      ));
      const names = (data.forwarded_to || []).map((u) => u.name).join(', ');
      toast.success(`Forwarded to ${names || 'teammate'}`);
      setForwardingNote(null);
      onMutated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not forward note');
    }
  };

  return (
    <>
      {writable && (
        <div className="mb-3 space-y-2">
          {/* Reply target chip — visible only when user is mid-reply */}
          {replyToNote && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={{ background: 'rgba(30,136,229,0.08)', border: '1px solid rgba(30,136,229,0.3)' }}
              data-testid="job-note-reply-chip"
            >
              <CornerDownRight className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate" style={{ color: '#94A3B8' }}>
                Replying to <strong className="text-white">{replyToNote.user_name}</strong>:{' '}
                <span style={{ color: '#64748B' }}>{replyToNote.text}</span>
              </span>
              <button
                onClick={() => setReplyToNote(null)}
                className="text-slate-400 hover:text-white flex-shrink-0"
                title="Cancel reply"
                data-testid="job-note-reply-cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
              placeholder={replyToNote ? `Reply to ${replyToNote.user_name}…` : 'Add a progress note…'}
              className="flex-1 px-3 py-2 rounded-lg text-sm text-white"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              data-testid="job-note-input"
              disabled={savingNote}
            />
            <Button
              onClick={addNote}
              disabled={savingNote || !newNote.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="job-note-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <BellRing className="w-3.5 h-3.5 flex-shrink-0" style={{ color: notifyTo === 'none' ? '#64748B' : '#1E88E5' }} />
            <label htmlFor="notify-select" className="flex-shrink-0" style={{ color: '#94A3B8' }}>
              Notify:
            </label>
            <select
              id="notify-select"
              value={notifyTo}
              onChange={(e) => setNotifyTo(e.target.value)}
              className="flex-1 max-w-[14rem] px-2 py-1 rounded text-xs text-white"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              data-testid="job-note-notify-select"
              disabled={savingNote}
            >
              <option value="none">No one (silent note)</option>
              <option value="assignee">Assignee only</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Job creator (admin)</option>
              <option value="all">Assignee + supervisor + admin</option>
            </select>
            {notifyTo !== 'none' && (
              <span className="text-[10px]" style={{ color: '#64748B' }}>
                bell + email
              </span>
            )}
          </div>
        </div>
      )}
      {notes.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>
          No notes yet. {writable ? 'Add the first one above.' : ''}
        </p>
      ) : (
        <ul className="space-y-2">
          {[...notes].reverse().map((n) => {
            const mine = n.user_id === currentUser?.id;
            const isStaff = ['admin', 'supervisor'].includes(currentUser?.role);
            const canDelete = mine || isStaff;
            const canEditNote = mine || isStaff;
            // Find the parent note for reply-threading visual indent
            const parent = n.reply_to_note_id
              ? notes.find((p) => p.id === n.reply_to_note_id)
              : null;
            return (
              <li
                key={n.id}
                className="p-3 rounded-lg group"
                style={{
                  background: '#0F1923',
                  border: '1px solid #2D3748',
                  marginLeft: parent ? '1.5rem' : 0,
                  borderLeft: parent ? '2px solid rgba(30,136,229,0.5)' : '1px solid #2D3748',
                }}
                data-testid={`job-note-${n.id}`}
              >
                {parent && (
                  <div
                    className="flex items-center gap-1 text-[10px] mb-1.5 truncate"
                    style={{ color: '#64748B' }}
                    title={parent.text}
                    data-testid={`job-note-reply-context-${n.id}`}
                  >
                    <CornerDownRight className="w-2.5 h-2.5 flex-shrink-0" />
                    <span>
                      Reply to <strong className="text-slate-300">{parent.user_name}</strong>:{' '}
                      <span style={{ color: '#475569' }}>
                        {parent.text.length > 60 ? parent.text.slice(0, 60) + '…' : parent.text}
                      </span>
                    </span>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-semibold text-white flex items-center gap-1.5">
                    {n.user_name}
                    {Array.isArray(n.notify_recipient_ids) && n.notify_recipient_ids.length > 0 && (
                      <span
                        className="text-[9px] font-medium px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(30,136,229,0.15)', color: '#7BB8E8' }}
                        title="Recipients were notified by bell + email"
                        data-testid={`job-note-notified-badge-${n.id}`}
                      >
                        📩 notified
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2">
                    <time
                      className="text-[10px]"
                      style={{ color: '#64748B' }}
                      dateTime={n.created_at}
                      title={fmtFull(n.created_at)}
                      data-testid={`job-note-timestamp-${n.id}`}
                    >
                      {fmt(n.created_at)}
                    </time>
                    {writable && !mine && (
                      <button
                        onClick={() => setReplyToNote(n)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: '#94A3B8' }}
                        title="Reply"
                        data-testid={`job-note-reply-${n.id}`}
                      >
                        <CornerDownRight className="w-3 h-3" />
                      </button>
                    )}
                    {writable && (
                      <button
                        onClick={() => setForwardingNote(n)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: '#94A3B8' }}
                        title="Forward to a teammate"
                        data-testid={`job-note-forward-${n.id}`}
                      >
                        <Send className="w-3 h-3" />
                      </button>
                    )}
                    {canEditNote && (
                      <button
                        onClick={() => beginEditNote(n)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: '#94A3B8' }}
                        title="Edit note"
                        data-testid={`job-note-edit-${n.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => deleteNote(n.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                        title="Delete note"
                        data-testid={`job-note-delete-${n.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                {editingNoteId === n.id ? (
                  <div className="mt-2 space-y-1" data-testid={`job-note-editor-${n.id}`}>
                    <Textarea
                      value={editingNoteText}
                      onChange={(e) => setEditingNoteText(e.target.value)}
                      rows={3}
                      maxLength={4000}
                      className="bg-[#0F1923] border-[#2D3748] text-white text-sm"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={cancelEditNote} disabled={savingNote}>Cancel</Button>
                      <Button size="sm" onClick={commitEditNote} disabled={savingNote || !editingNoteText.trim()} className="bg-blue-600 hover:bg-blue-700">
                        {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: '#CBD5E1' }}>
                    {n.text}
                    {n.edited_at && (
                      <span className="text-[10px] text-slate-500 ml-2" title={`Edited ${fmtFull(n.edited_at)}${n.edited_by_name ? ' by ' + n.edited_by_name : ''}`}>
                        (edited)
                      </span>
                    )}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ForwardNoteDialog
        open={!!forwardingNote}
        note={forwardingNote}
        onClose={() => setForwardingNote(null)}
        onSubmit={handleForwardNote}
      />
    </>
  );
}
